import { api } from './api'
import type {
  BenchmarkSource,
  Dashboard,
  DashboardListItem,
  DashboardWidget,
  GridLayoutItem,
  Goal,
  MeasuresRegistry,
  WidgetConfig,
  PortfolioGroup,
} from '../types/exec'

export const execAPI = {
  // ── Dashboards ──────────────────────────────────────────────────

  getDashboards() {
    return api.get<DashboardListItem[]>('/api/exec/dashboards/')
  },

  getDashboard(id: string) {
    return api.getRaw<Dashboard>(`/api/exec/dashboards/${id}/`)
  },

  createDashboard(data: { name: string; office?: number | null; is_default?: boolean; layout?: GridLayoutItem[] }) {
    return api.post<Dashboard>('/api/exec/dashboards/', data)
  },

  updateDashboard(id: string, data: Partial<{ name: string; office: number | null; is_default: boolean }>) {
    return api.patch<Dashboard>(`/api/exec/dashboards/${id}/`, data)
  },

  updateLayout(dashboardId: string, layout: GridLayoutItem[]) {
    return api.patch<Dashboard>(`/api/exec/dashboards/${dashboardId}/update-layout/`, { layout })
  },

  deleteDashboard(id: string) {
    return api.delete(`/api/exec/dashboards/${id}/`)
  },

  // ── Widgets ─────────────────────────────────────────────────────

  addWidget(dashboardId: string, data: { measure: string; viz_type?: string; config?: WidgetConfig; title?: string }) {
    return api.post<DashboardWidget>(`/api/exec/dashboards/${dashboardId}/widgets/`, data)
  },

  updateWidget(dashboardId: string, widgetId: string, data: Partial<DashboardWidget>) {
    return api.patch<DashboardWidget>(`/api/exec/dashboards/${dashboardId}/widgets/${widgetId}/`, data)
  },

  removeWidget(dashboardId: string, widgetId: string) {
    return api.delete(`/api/exec/dashboards/${dashboardId}/widgets/${widgetId}/`)
  },

  // ── Goals ───────────────────────────────────────────────────────

  getGoals(params?: { office?: number; measure?: string }) {
    return api.get<Goal[]>('/api/exec/goals/', params)
  },

  createGoal(data: Omit<Goal, 'id' | 'set_by' | 'set_by_name' | 'office_name' | 'portfolio_group_name' | 'created_at'>) {
    return api.post<Goal>('/api/exec/goals/', data)
  },

  updateGoal(id: string, data: Partial<Goal>) {
    return api.patch<Goal>(`/api/exec/goals/${id}/`, data)
  },

  deleteGoal(id: string) {
    return api.delete(`/api/exec/goals/${id}/`)
  },

  suggestBenchmarks(portfolioGroupId: string) {
    return api.getRaw<{ suggestions: BenchmarkSource[] }>(
      '/api/exec/goals/suggest-benchmarks/',
      { portfolio_group: portfolioGroupId },
    )
  },

  // ── Portfolio Groups ─────────────────────────────────────────────

  getPortfolioGroups() {
    return api.get<PortfolioGroup[]>('/api/exec/portfolio-groups/')
  },

  getPortfolioGroup(id: string) {
    return api.getRaw<PortfolioGroup>(`/api/exec/portfolio-groups/${id}/`)
  },

  createPortfolioGroup(data: { name: string; description?: string }) {
    return api.post<PortfolioGroup>('/api/exec/portfolio-groups/', data)
  },

  updatePortfolioGroup(id: string, data: { name?: string; description?: string }) {
    return api.patch<PortfolioGroup>(`/api/exec/portfolio-groups/${id}/`, data)
  },

  deletePortfolioGroup(id: string) {
    return api.delete(`/api/exec/portfolio-groups/${id}/`)
  },

  addPortfolioGroupRule(groupId: string, rule: { hierarchy_level: string; entity_code: string; entity_name: string }) {
    return api.post<PortfolioGroup>(`/api/exec/portfolio-groups/${groupId}/rules/`, rule)
  },

  removePortfolioGroupRule(groupId: string, ruleId: string) {
    return api.delete<PortfolioGroup>(`/api/exec/portfolio-groups/${groupId}/rules/${ruleId}/`)
  },

  resolvePortfolioGroup(id: string) {
    return api.getRaw<{ resolved_office_count: number; resolved_office_codes: string[]; total_offices: number }>(
      `/api/exec/portfolio-groups/${id}/resolve/`,
    )
  },

  // ── Registry ────────────────────────────────────────────────────

  getMeasuresRegistry() {
    return api.getRaw<MeasuresRegistry>('/api/exec/measures/')
  },
}
